import { createContext, useContext, type ReactNode } from "react";
import type { AxiosInstance } from "axios";

interface ViewConfigurationEnvironment {
  axiosInstance: AxiosInstance;
  affiliateId: string | null;
}

const ViewConfigurationClientContext =
  createContext<ViewConfigurationEnvironment | null>(null);

export interface ViewConfigurationClientProviderProps {
  axiosInstance: AxiosInstance;
  affiliateId: string | null;
  children: ReactNode;
}

// Lets each consuming app (my, portal, ...) supply its own pre-configured
// axios instance (auth + affiliate baseURL) and affiliate id (used to scope
// query keys per tenant).
export const ViewConfigurationClientProvider = ({
  axiosInstance,
  affiliateId,
  children,
}: ViewConfigurationClientProviderProps) => (
  <ViewConfigurationClientContext.Provider
    value={{ axiosInstance, affiliateId }}
  >
    {children}
  </ViewConfigurationClientContext.Provider>
);

export const useViewConfigurationClient = (): AxiosInstance => {
  return useViewConfigurationEnvironment().axiosInstance;
};

export const useViewConfigurationAffiliateId = (): string | null => {
  return useViewConfigurationEnvironment().affiliateId;
};

const useViewConfigurationEnvironment = (): ViewConfigurationEnvironment => {
  const environment = useContext(ViewConfigurationClientContext);

  if (!environment) {
    throw new Error(
      "useViewConfigurationClient must be used within a ViewConfigurationClientProvider",
    );
  }

  return environment;
};
